import { z } from "zod";
import apiClient from "../../../shared/api/client";
import type {
  ChangelogEntry,
  ChangelogList,
  CreateChangelogEntryData,
  ListParams,
  UpdateChangelogEntryData,
} from "../types";

const changelogKindSchema = z.enum([
  "feature",
  "improvement",
  "fix",
  "breaking",
  "security",
]);
const changelogStatusSchema = z.enum(["draft", "published"]);

const changelogEntrySchema = z.object({
  id: z.string(),
  version: z.string(),
  title: z.string(),
  body: z.string(),
  kind: changelogKindSchema,
  status: changelogStatusSchema,
  tags: z.array(z.string()).default([]),
  publishedAt: z.string().nullable(),
  authorId: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

const changelogListSchema = z.object({
  data: z.array(changelogEntrySchema),
  total: z.number(),
  page: z.number(),
  limit: z.number(),
  totalPages: z.number(),
});

const buildListParams = (params?: ListParams) => {
  const merged: Record<string, string | number> = {
    status: params?.status ?? "all",
  };
  if (params?.page !== undefined) merged.page = params.page;
  if (params?.limit !== undefined) merged.limit = params.limit;
  if (params?.kind !== undefined && params.kind !== "all")
    merged.kind = params.kind;
  if (params?.search) merged.search = params.search;
  return merged;
};

export const getEntries = async (
  params?: ListParams,
): Promise<ChangelogList> => {
  const response = await apiClient.get("/admin/changelog", {
    params: buildListParams(params),
  });
  return changelogListSchema.parse(response.data);
};

export const getEntry = async (id: string): Promise<ChangelogEntry> => {
  const response = await apiClient.get(`/admin/changelog/${id}`);
  return changelogEntrySchema.parse(response.data);
};

export const createEntry = async (
  data: CreateChangelogEntryData,
): Promise<ChangelogEntry> => {
  const response = await apiClient.post("/admin/changelog", data);
  return changelogEntrySchema.parse(response.data);
};

export const updateEntry = async (
  id: string,
  data: UpdateChangelogEntryData,
): Promise<ChangelogEntry> => {
  const response = await apiClient.patch(`/admin/changelog/${id}`, data);
  return changelogEntrySchema.parse(response.data);
};

export const deleteEntry = async (id: string): Promise<void> => {
  await apiClient.delete(`/admin/changelog/${id}`);
};

export const publishEntry = async (id: string): Promise<ChangelogEntry> => {
  const response = await apiClient.post(`/admin/changelog/${id}/publish`);
  return changelogEntrySchema.parse(response.data);
};

export const unpublishEntry = async (id: string): Promise<ChangelogEntry> => {
  const response = await apiClient.post(`/admin/changelog/${id}/unpublish`);
  return changelogEntrySchema.parse(response.data);
};

export const changelogApi = {
  getEntries,
  getEntry,
  createEntry,
  updateEntry,
  deleteEntry,
  publishEntry,
  unpublishEntry,
};
